import React, { useState, useEffect, useContext } from 'react';
import './style.css';
import { Laugh, Smile, Meh, Frown, ArrowLeft, ArrowRight, MoveUp } from 'lucide-react';
import CalculateRequiredIncomeToAfford from '/src/services/CalculateRequiredIncomeToAfford';
import { CurlyBraceDivider } from '/src/Components/CurlyBraceDivider/CurlyBraceDivider';
import LTVCalculators from '/src/services/LTVCalculators';
import { AddressSearchContext, WindowSizeContext } from '/src/App';
import { usePropertyGaLogging } from '/src/contexts/PropertyGaLoggingContext';

const LTVAttractivenessIncomeRequiredToday = ({
    currentStageData,
    originalValues,
    currentHomeValue,
    setCurrentHomeValue,
    interestRatePercent,
    setInterestRatePercent,
    downPaymentPercent,
    setDownPaymentPercent,
    handleReset,
    incomePercentages,
}) => {
    const { deviceType } = useContext(WindowSizeContext);
    const [showAssumptions, setShowAssumptions] = useState(false);
    // const { LOG_GA_EVENT } = useContext(AddressSearchContext);
    const { handleLoggingGaEventFromProperty } = usePropertyGaLogging();

    // useEffect to log when showAssumptions is toggled to true
    useEffect(() => {
        if (showAssumptions) {
            handleLoggingGaEventFromProperty('open_market_appeal_ltv_area_households_change_assumptions');
        }
    }, [showAssumptions]);

    const asYouCanSeeText = "As you can see, when somebody stretches their budget they need lower household incomes to afford the home and the pool of buyers increases. However, it isn’t wise to assume buyers are willing (or able) take on this risk…"
    
    return (
        <div className="ltv-attractiveness-content">
                <h2 className="ltv-attractiveness-title">{currentStageData.title}</h2>

            <div className={`ltv-attractiveness-stage-three ${deviceType}`}>
                <div className="ltv-attractiveness-assumptions-column">
                    <span className="ltv-attractiveness-headline text-align-center">
                        Let's say a buyer comes along and wants to buy this home...
                    </span>
                    <div className={`ltv-attractiveness-assumptions ${showAssumptions ? '' : 'hide-assumptions'}`}>
                        <div className="ltv-attractiveness-input-group">
                            <label>Home Value</label>
                            <div className="ltv-attractiveness-input-wrapper">
                                <input
                                    type="range"
                                    min={Math.round((originalValues.currentHomeValue / 2) / 25000) * 25000}
                                    max={Math.round((originalValues.currentHomeValue * 1.5) / 25000) * 25000}
                                    step={25000}
                                    value={currentHomeValue}
                                    onChange={(e) => {
                                        setCurrentHomeValue(Number(e.target.value));
                                    }}
                                    className="ltv-attractiveness-slider"
                                />
                                <span className="ltv-attractiveness-input-value">
                                    {LTVCalculators.formatCurrency(currentHomeValue)}
                                </span>
                            </div>
                        </div>
                        <div className="ltv-attractiveness-input-group">
                            <label>Interest Rate</label>
                            <div className="ltv-attractiveness-input-wrapper">
                                <input
                                    type="range"
                                    min={0}
                                    max={10}
                                    step={0.5}
                                    value={interestRatePercent * 100}
                                    onChange={(e) => {
                                        setInterestRatePercent(Number(e.target.value) / 100);
                                    }}
                                    className="ltv-attractiveness-slider"
                                />
                                <span className="ltv-attractiveness-input-value">
                                    {(interestRatePercent * 100).toFixed(1)}%
                                </span>
                            </div>
                        </div>
                        <div className="ltv-attractiveness-input-group">
                            <label>Buyer Puts Down</label>
                            <div className="ltv-attractiveness-input-wrapper">
                                <input
                                    type="range"
                                    min={0}
                                    max={50}
                                    step={5}
                                    value={downPaymentPercent * 100}
                                    onChange={(e) => {
                                        setDownPaymentPercent(Number(e.target.value) / 100);
                                    }}
                                    className="ltv-attractiveness-slider"
                                />
                                <span className="ltv-attractiveness-input-value">
                                    {(downPaymentPercent * 100).toFixed(0)}%
                                </span>
                            </div>
                        </div>
                        {(currentHomeValue !== originalValues.currentHomeValue ||
                            interestRatePercent !== originalValues.interestRate ||
                            downPaymentPercent !== originalValues.downPayment) && (
                                <button
                                    className="ltv-attractiveness-reset-button"
                                    onClick={handleReset}
                                >
                                    Reset to Original Values
                                </button>
                            )}
                    </div>
                    {!showAssumptions && (
                        <button className="ltv-attractiveness-show-assumptions-button income" onClick={() => setShowAssumptions(true)}>
                            Change Assumptions
                        </button>
                    )}
                </div>
                <CurlyBraceDivider direction={deviceType === 'mobile' ? 'down' : 'right'} color='var(--dark-grey)' />
                <div className="ltv-attractiveness-results-column">
                    <span className="ltv-attractiveness-headline text-align-center">
                        Based on the current expenses for this home, here is what a buyer would need across different levels of spend devoted to housing:
                    </span>
                    <div className="ltv-attractiveness-percentage-grid">
                        {incomePercentages.map(({ icon: Icon, percentage, level, requiredIncome }) => (
                            <div key={percentage} className="ltv-attractiveness-percentage-item-frame">
                                <div key={percentage} className={`ltv-attractiveness-percentage-item ${level}`}>
                                    <Icon className="ltv-attractiveness-icon" />
                                    <span className="ltv-attractiveness-percentage">{percentage}</span>
                                </div>
                                <span className="ltv-attractiveness-income">
                                    {Number(requiredIncome).toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                        maximumFractionDigits: 0
                                    })}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <p className="ltv-attractiveness-description text-align-center" dangerouslySetInnerHTML={{ __html: asYouCanSeeText }} />
        </div>
    );
};

export default LTVAttractivenessIncomeRequiredToday;